import type { ApiNode, Input } from "@/workflow_api/types/workflow";
import type { NodeVariable, Variable } from "../../types";
import type {
  ValueColumn,
  ValueInputType,
  FieldExpirationConfig,
  ExpirationTimeUnit,
  LinkActionType,
  LinkUserEntry,
  LinkExpedientsActionType,
  LinkExpedientEntry,
} from "../types";
import type { EntityRoleType } from "@/schemas/digital-files/entity";

/**
 * Helper type for flat input from API
 */
type FlatInput = Input[number];

/**
 * Maps entity field_type to ValueInputType
 */
const mapFieldTypeToValueInputType = (fieldType: string): ValueInputType => {
  switch (fieldType.toLowerCase()) {
    case "text":
    case "textarea":
    case "email":
    case "phone":
    case "select":
    case "multiselect":
    case "radio":
    case "identification":
    case "string":
    case "varchar":
    case "char":
      return "string";
    case "number":
    case "currency":
    case "int":
    case "integer":
    case "float":
    case "double":
    case "decimal":
      return "number";
    case "date":
    case "datetime":
    case "time":
    case "date_range":
    case "datetime_range":
    case "timestamp":
      return "date";
    case "checkbox":
    case "boolean":
    case "bool":
      return "boolean";
    default:
      return "string";
  }
};

/**
 * Validates if a string represents a valid date
 */
const isValidDateString = (dateString: string): boolean => {
  if (!dateString || typeof dateString !== "string") return false;
  const date = new Date(dateString);
  return !isNaN(date.getTime());
};

/**
 * Check and parse value according to type
 */
const tryParseValueType = (value: string, type: ValueInputType): string => {
  switch (type) {
    case "number": {
      try {
        const parsed = Number(value);
        return isNaN(parsed) ? "" : value;
      } catch {
        return "";
      }
    }
    case "boolean": {
      try {
        if (typeof value === "boolean") {
          return String(value);
        }
        if (typeof value === "string") {
          const lowered = value.toLowerCase();
          if (lowered === "true" || lowered === "false") {
            return value;
          }
        }
        return "";
      } catch {
        return "";
      }
    }
    case "date":
      return isValidDateString(String(value)) ? value : "";
    case "string":
      return value;
    default:
      return value;
  }
};

/**
 * Helper function to get placeholder text based on column type
 */
const getPlaceholderForType = (type: string): string => {
  switch (type) {
    case "string":
    case "text":
      return "Escribe un texto";
    case "number":
      return "Escribe un número";
    case "date":
      return "Selecciona una fecha";
    case "boolean":
      return "Elige verdadero o falso";
    case "variable":
      return "Elige una variable";
    default:
      return "Ingresa un valor";
  }
};

/**
 * Expediente field data format from API
 */
interface ExpedienteFieldData {
  name: string;
  field_type: string;
  is_required: boolean;
  value: string | boolean;
  timer_config?: {
    type: string;
    expires_date?: string;
    notification_date?: string;
    expires_amount?: number;
    expires_unit?: string;
    notification_amount?: number;
    notification_unit?: string;
    replace_value?: string;
  };
}

/**
 * Deserialize timer_config from API to FieldExpirationConfig
 */
const deserializeTimerConfig = (
  timerConfig: ExpedienteFieldData["timer_config"],
): FieldExpirationConfig | undefined => {
  if (!timerConfig) return undefined;

  // Map API type to internal type
  const type =
    timerConfig.type === "expiration_date"
      ? "expiration_date"
      : "expiration_time";

  // Detect if replace_value is a variable (mustache template format: {{variable_name}})
  const replaceValue = timerConfig.replace_value || "";
  const isMustacheVariable = /^\{\{.+\}\}$/.test(replaceValue);
  const replacementInputType: "inp" | "var" = isMustacheVariable
    ? "var"
    : "inp";
  // Extract variable name from mustache template if it's a variable
  const parsedReplaceValue = isMustacheVariable
    ? replaceValue.slice(2, -2) // Remove {{ and }}
    : replaceValue;

  if (type === "expiration_date") {
    // Detect if expires_date is a variable
    const expiresDateValue = timerConfig.expires_date || "";
    const isExpiresDateVariable = /^\{\{.+\}\}$/.test(expiresDateValue);
    const expirationDateInputType: "inp" | "var" = isExpiresDateVariable
      ? "var"
      : "inp";
    const parsedExpirationDate = isExpiresDateVariable
      ? expiresDateValue.slice(2, -2)
      : expiresDateValue;

    // Detect if notification_date is a variable
    const notificationDateValue = timerConfig.notification_date || "";
    const isNotificationDateVariable = /^\{\{.+\}\}$/.test(
      notificationDateValue,
    );
    const notificationDateInputType: "inp" | "var" = isNotificationDateVariable
      ? "var"
      : "inp";
    const parsedNotificationDate = isNotificationDateVariable
      ? notificationDateValue.slice(2, -2)
      : notificationDateValue;

    return {
      type: "expiration_date",
      expirationDate: parsedExpirationDate || undefined,
      expirationDateInputType,
      notificationDate: parsedNotificationDate || undefined,
      notificationDateInputType,
      replacementValue: parsedReplaceValue,
      replacementInputType,
    };
  } else {
    return {
      type: "expiration_time",
      expirationAmount: timerConfig.expires_amount,
      expirationUnit: timerConfig.expires_unit as ExpirationTimeUnit,
      notificationAmount: timerConfig.notification_amount,
      notificationUnit: timerConfig.notification_unit as ExpirationTimeUnit,
      replacementValue: parsedReplaceValue,
      replacementInputType,
    };
  }
};

/**
 * Expediente data format from API (inside "data" input) - Legacy format
 */
interface ExpedienteDataObject {
  expedient_base_entity_id: string;
  expedient_register_id: string;
  name?: string;
  fields?: Record<string, ExpedienteFieldData>;
}

/**
 * Get the expediente data object from node inputs
 * Supports both legacy format (JSON in "data" input) and new flat format
 *
 * @param node - The API node
 * @returns The parsed expediente data object or undefined
 */
export const getExpedienteDataFromNode = (
  node: ApiNode,
): ExpedienteDataObject | undefined => {
  // First, try new flat format
  const entityIdInput = node.inputs?.find(
    (input) => input.name === "expedient_base_entity_id",
  );
  const registerIdInput = node.inputs?.find(
    (input) => input.name === "expedient_register_id",
  );

  if (entityIdInput) {
    // New flat format detected
    const result: ExpedienteDataObject = {
      expedient_base_entity_id: getInputValue(entityIdInput) || "",
      expedient_register_id: getInputValue(registerIdInput) || "",
    };

    // Get registry name from new format
    const registryNameInput = node.inputs?.find(
      (input) => input.name === "expedient_register_name",
    );
    if (registryNameInput) {
      result.name = getInputValue(registryNameInput) || "";
    }

    return result;
  }

  // Legacy format: look for "data" input
  const dataInput = node.inputs?.find((input) => input.name === "data");
  if (!dataInput || !dataInput.value) {
    return undefined;
  }

  try {
    if (typeof dataInput.value === "string") {
      return JSON.parse(dataInput.value) as ExpedienteDataObject;
    } else if (
      typeof dataInput.value === "object" &&
      dataInput.value !== null
    ) {
      return dataInput.value as unknown as ExpedienteDataObject;
    }
    return undefined;
  } catch (e) {
    console.error("[Expediente] Error parsing data input:", e);
    return undefined;
  }
};

/**
 * Helper to get the value from a flat input, handling connection/field cases
 * Supports both legacy format (connection field) and new format (source_connections array)
 */
const getInputValue = (input: FlatInput | undefined): string | null => {
  if (!input) return null;

  if (input.source_connections && input.source_connections.length > 0) {
    const sourceConn = input.source_connections[0];
    if (sourceConn?.field_id && sourceConn?.output_name) {
      return `{{${sourceConn.field_id}-${sourceConn.output_name}}}`;
    }
    if (sourceConn?.field_id) {
      return `{{${sourceConn.field_id}}}`;
    }
    if (sourceConn?.output_name) {
      return `{{${sourceConn.source_node_id}-${sourceConn.output_name}}}`;
    }
  }

  return input.value?.toString() ?? null;
  // // Check legacy format with connection field
  // if (input.connection) {
  //   // Variable reference - return mustache template for backwards compatibility
  //   if (input.field) {
  //     return `{{${input.field}}}`;
  //   }
  //   if (input.output_name) {
  //     return `{{${input.output_name}}}`;
  //   }
  //   return null;
  // }

  // // Check new format with source_connections array
  // if (input.source_connections && input.source_connections.length > 0) {
  //   const sourceConn = input.source_connections[0];
  //   // Variable reference - return mustache template using field_id or output_name
  //   if (sourceConn.field_id) {
  //     return `{{${sourceConn.field_id}}}`;
  //   }
  //   if (sourceConn.output_name) {
  //     return `{{${sourceConn.output_name}}}`;
  //   }
  //   return null;
  // }

  // return input.value?.toString() ?? null;
};

/**
 * Helper to check if an input has a variable connection
 * Handles both legacy format (connection field) and new format (source_connections array)
 * Also handles global variables (value: "global-variables")
 */
const hasVariableConnection = (input: FlatInput | undefined): boolean => {
  if (!input) return false;
  // Legacy format
  if (input.connection) return true;
  // New format with source_connections array
  if (input.source_connections && input.source_connections.length > 0)
    return true;
  // Global variables format
  if (input.global_type) return true;
  return false;
};

/**
 * Helper to resolve a variable from a flat input
 * Handles both legacy format (connection/field) and new format (source_connections array)
 * Also handles global variables (value: "global-variables")
 */
const resolveVariableFromInput = (
  input: FlatInput | undefined,
  inputVariables: NodeVariable[],
): Variable | null => {
  if (!input) return null;

  console.log(input);

  // Check for global variables first
  if (input.global_type) {
    if (
      input.global_type === "NODE_EXECUTION_FINISH" ||
      input.global_type === "NODE_EXECUTION_START"
    ) {
      const nodeId = input?.source_connections?.[0]?.source_node_id;
      const variable = inputVariables
        .find((iv) => iv.nodeId === nodeId)
        ?.variables.find((v) => v.global_type === input.global_type);
      if (variable) {
        return {
          ...variable,
        };
      }
      return null;
    }

    // Find the global variables node
    const globalVarNode = inputVariables.find(
      (nv) => nv.type === "global-variables",
    );
    if (globalVarNode) {
      // Find the specific global variable by global_type
      const variable = globalVarNode.variables.find(
        (v) => v.global_type === input.global_type,
      );
      if (variable) {
        return {
          ...variable,
          node_id: globalVarNode.nodeId,
        };
      }
    }
    return null;
  }

  let nodeId: string | undefined;
  let fieldId: string | undefined;
  let outputName: string | undefined;

  // Check legacy format first
  if (input.connection) {
    nodeId = input.connection;
    fieldId = input.field;
    outputName = input.output_name;
  }
  // Check new format with source_connections
  else if (input.source_connections && input.source_connections.length > 0) {
    const sourceConn = input.source_connections[0];
    nodeId = sourceConn.source_node_id;
    fieldId = sourceConn.field_id;
    outputName = sourceConn.output_name;
  }

  if (!nodeId) return null;

  const nodeVar = inputVariables.find((nv) => nv.nodeId === nodeId);
  if (!nodeVar) return null;

  // For expanded data center select variables, we need to look for the composite ID
  // The variable ID format is: ${field_id}-${output_name}
  if (fieldId && outputName) {
    const compositeId = `${fieldId}-${outputName}`;
    const variable = nodeVar.variables.find((v) => v.id === compositeId);
    if (variable) {
      return {
        ...variable,
        node_id: nodeVar.nodeId,
      };
    }
  }

  // Find the variable by field_id or output_name
  if (fieldId) {
    // First try to find by id
    const variable = nodeVar.variables.find((v) => v.id === fieldId);

    if (variable) {
      return {
        ...variable,
        node_id: nodeVar.nodeId,
      };
    }
  }

  if (outputName) {
    const variable = nodeVar.variables.find(
      (v) => v.id === `${nodeId}-${outputName}`,
    );
    if (variable) {
      return {
        ...variable,
        node_id: nodeVar.nodeId,
      };
    }
  }

  const variable = nodeVar.variables.find((v) => v.id === nodeId);

  if (variable) {
    return {
      ...variable,
      node_id: nodeVar.nodeId,
    };
  }

  // // If no specific field, return the first variable from the node
  // if (nodeVar.variables.length > 0) {
  //   return {
  //     ...nodeVar.variables[0],
  //     node_id: nodeVar.nodeId,
  //   };
  // }

  return null;
};

/**
 * Deserialize columns data specifically for Expediente node
 * Handles both legacy format (data.fields) and new flat format with positional inputs
 *
 * @param node - The API node
 * @param columnsDef - Column definitions (from entity fields)
 * @param inputVariables - Available input variables
 * @returns Deserialized value columns
 */
export const getDeserializedExpedienteColumns = (
  node: ApiNode,
  columnsDef: Variable[],
  inputVariables: NodeVariable[],
): ValueColumn[] => {
  // Check if this is new flat format by looking for expedient_base_fields_id input
  const fieldsIdInput = node.inputs?.find(
    (input) => input.name === "expedient_base_fields_id",
  );

  if (fieldsIdInput && Array.isArray(fieldsIdInput.value)) {
    // New flat format
    const result = deserializeColumnsFromFlatFormat(
      node,
      columnsDef,
      inputVariables,
    );
    return result;
  }

  // Legacy format: Get the data object which contains everything
  const dataObject = getExpedienteDataFromNode(node);
  if (!dataObject || !dataObject.fields) {
    return [];
  }

  const fieldsData = dataObject.fields;

  const columns: ValueColumn[] = [];

  // Get flat list of all input variables
  const allInputVariables = inputVariables.flatMap((nodeVar) =>
    (nodeVar.variables || []).map((v) => ({
      ...v,
      node_id: nodeVar.nodeId,
    })),
  );

  // Use column definitions to create proper columns
  columnsDef.forEach((columnDef) => {
    // In expediente format, the key is the field ID
    const fieldData = fieldsData[columnDef.id];

    let value: string | Variable = "";
    let inputType: "input" | "variable" = "input";
    // Use field_type from backend if available, otherwise fall back to columnDef type
    const valueType = fieldData?.field_type
      ? mapFieldTypeToValueInputType(fieldData.field_type)
      : mapFieldTypeToValueInputType(columnDef.type);

    if (fieldData !== undefined) {
      // Get the raw value from the field data
      const rawValue = String(fieldData.value);

      // Check if it's a variable reference ({{variable_name}})
      if (rawValue.startsWith("{{") && rawValue.endsWith("}}")) {
        const variableName = rawValue.slice(2, -2);
        const variable = allInputVariables.find((v) => v.name === variableName);
        if (variable) {
          value = variable;
          inputType = "variable";
        }
      } else {
        value = tryParseValueType(rawValue, valueType);
        inputType = "input";
      }
    }

    columns.push({
      name: columnDef.id,
      value,
      inputType,
      valueType,
      label: columnDef.name,
      placeholder: getPlaceholderForType(valueType),
      isRequired: fieldData?.is_required ?? false,
      fieldType: fieldData?.field_type,
      expiration: deserializeTimerConfig(fieldData?.timer_config),
    });
  });

  return columns;
};

/**
 * Deserialize columns from new flat format with positional inputs
 * TODO: Match by a reliable key instead of position once we identify what the backend returns
 */
const deserializeColumnsFromFlatFormat = (
  node: ApiNode,
  columnsDef: Variable[],
  inputVariables: NodeVariable[],
): ValueColumn[] => {
  const fieldsIdInput = node.inputs?.find(
    (input) => input.name === "expedient_base_fields_id",
  );
  const fieldIds = Array.isArray(fieldsIdInput?.value)
    ? (fieldsIdInput.value as string[])
    : [];

  if (fieldIds.length === 0) return [];

  // Get all inputs
  const allInputs = node.inputs || [];

  // Build lookup maps by reference_key for O(1) access
  const fieldInputsByRef = new Map<string, FlatInput>();
  const timerTypeByRef = new Map<string, FlatInput>();
  const timerExpiresValueByRef = new Map<string, FlatInput>();
  const timerExpiresUnitByRef = new Map<string, FlatInput>();
  const timerNotifyValueByRef = new Map<string, FlatInput>();
  const timerNotifyUnitByRef = new Map<string, FlatInput>();
  const timerReplaceValueByRef = new Map<string, FlatInput>();

  for (const input of allInputs) {
    const ref = input.reference_key;
    if (!ref) continue;
    switch (input.name) {
      case "fields":
        fieldInputsByRef.set(ref, input);
        break;
      case "timer_type":
        timerTypeByRef.set(ref, input);
        break;
      case "timer_expires_value":
        timerExpiresValueByRef.set(ref, input);
        break;
      case "timer_expires_unit":
        timerExpiresUnitByRef.set(ref, input);
        break;
      case "timer_notify_value":
        timerNotifyValueByRef.set(ref, input);
        break;
      case "timer_notify_unit":
        timerNotifyUnitByRef.set(ref, input);
        break;
      case "timer_replace_value":
        timerReplaceValueByRef.set(ref, input);
        break;
    }
  }

  const columns: ValueColumn[] = [];

  fieldIds.forEach((fieldId) => {
    // Find the column definition for this field
    const columnDef = columnsDef.find((c) => c.id === fieldId);
    if (!columnDef) return;

    // Get field value by reference_key (fieldId === entity field UUID === reference_key)
    const fieldInput = fieldInputsByRef.get(fieldId);
    let value: string | Variable | null = "";
    let inputType: "input" | "variable" = "input";

    if (fieldInput) {
      if (hasVariableConnection(fieldInput)) {
        // Variable reference (handles both legacy and new format)
        const variable = resolveVariableFromInput(fieldInput, inputVariables);
        if (variable) {
          value = variable;
          inputType = "variable";
        }
      } else if (
        fieldInput.value !== null &&
        fieldInput.value !== undefined &&
        fieldInput.value !== ""
      ) {
        // String value with actual content
        value = String(fieldInput.value);
        inputType = "input";
      }
      // null or empty string means no configuration - keep default (empty input mode)
    }

    // Get timer configuration by reference_key
    const timerTypeInput = timerTypeByRef.get(fieldId);
    const timerExpiresValueInput = timerExpiresValueByRef.get(fieldId);
    const timerExpiresUnitInput = timerExpiresUnitByRef.get(fieldId);
    const timerNotifyValueInput = timerNotifyValueByRef.get(fieldId);
    const timerNotifyUnitInput = timerNotifyUnitByRef.get(fieldId);
    const timerReplaceValueInput = timerReplaceValueByRef.get(fieldId);

    let expiration: FieldExpirationConfig | undefined;

    if (timerTypeInput?.value) {
      const timerType = String(timerTypeInput.value);
      if (timerType === "expiration_date") {
        // Get expiration date (can be string or Variable)
        let expirationDate: string | Variable | undefined;
        let expirationDateInputType: "inp" | "var" = "inp";
        if (hasVariableConnection(timerExpiresValueInput)) {
          const variable = resolveVariableFromInput(
            timerExpiresValueInput!,
            inputVariables,
          );
          if (variable) {
            expirationDate = variable;
            expirationDateInputType = "var";
          }
        } else {
          expirationDate =
            timerExpiresValueInput?.value?.toString() || undefined;
        }

        // Get notification date (can be string or Variable)
        let notificationDate: string | Variable | undefined;
        let notificationDateInputType: "inp" | "var" = "inp";
        if (hasVariableConnection(timerNotifyValueInput)) {
          const variable = resolveVariableFromInput(
            timerNotifyValueInput!,
            inputVariables,
          );
          if (variable) {
            notificationDate = variable;
            notificationDateInputType = "var";
          }
        } else {
          notificationDate =
            timerNotifyValueInput?.value?.toString() || undefined;
        }

        expiration = {
          type: "expiration_date",
          expirationDate,
          expirationDateInputType,
          notificationDate,
          notificationDateInputType,
          replacementValue: getReplacementValue(
            timerReplaceValueInput,
            inputVariables,
          ),
          replacementInputType: hasVariableConnection(timerReplaceValueInput)
            ? "var"
            : "inp",
        };
      } else if (timerType === "expiration_duration") {
        expiration = {
          type: "expiration_time",
          expirationAmount: timerExpiresValueInput?.value
            ? Number(timerExpiresValueInput.value)
            : undefined,
          expirationUnit:
            (timerExpiresUnitInput?.value?.toString() as ExpirationTimeUnit) ||
            undefined,
          notificationAmount: timerNotifyValueInput?.value
            ? Number(timerNotifyValueInput.value)
            : undefined,
          notificationUnit:
            (timerNotifyUnitInput?.value?.toString() as ExpirationTimeUnit) ||
            undefined,
          replacementValue: getReplacementValue(
            timerReplaceValueInput,
            inputVariables,
          ),
          replacementInputType: hasVariableConnection(timerReplaceValueInput)
            ? "var"
            : "inp",
        };
      }
    }

    const valueType = mapFieldTypeToValueInputType(columnDef.type);

    columns.push({
      name: fieldId,
      value,
      inputType,
      valueType,
      label: columnDef.name,
      placeholder: getPlaceholderForType(valueType),
      isRequired: false, // Will be overridden by entity field definition
      // Don't set fieldType here - it will be updated from entity fields in the merge
      expiration,
    });
  });

  return columns;
};

/**
 * Helper to get replacement value from timer_replace_value input
 */
const getReplacementValue = (
  input: FlatInput | undefined,
  inputVariables: NodeVariable[],
): string | Variable => {
  if (!input) return "";
  if (hasVariableConnection(input)) {
    const variable = resolveVariableFromInput(input, inputVariables);
    return variable || "";
  }
  return input.value?.toString() || "";
};

/**
 * Deserialize link users data from node
 * Supports both legacy format (data.users) and new flat format (user_document, user_email, user_role)
 * @param node - The API node
 * @param entityRoles - Available roles for the entity
 * @returns Object with linkAction and linkUsers array, or undefined
 */
export const getDeserializedLinkUsers = (
  node: ApiNode,
  entityRoles: EntityRoleType[],
  inputVariables: NodeVariable[],
): { linkAction: LinkActionType; linkUsers: LinkUserEntry[] } | undefined => {
  const operationInput = node.inputs?.find(
    (input) => input.name === "operation",
  );
  const operation = operationInput?.value?.toString().toUpperCase();

  if (operation !== "LINK_USERS" && operation !== "UNLINK_USERS") {
    return undefined;
  }

  const linkAction: LinkActionType =
    operation === "LINK_USERS" ? "link" : "unlink";

  // Check for new flat format by looking for user_document inputs
  const userDocumentInputs =
    node.inputs?.filter((input) => input.name === "user_document") || [];

  if (userDocumentInputs.length > 0) {
    // New flat format
    return deserializeLinkUsersFromFlatFormat(
      node,
      entityRoles,
      inputVariables,
      linkAction,
    );
  }

  // Legacy format
  const dataObject = getExpedienteDataFromNode(node);
  if (!dataObject) {
    return {
      linkAction,
      linkUsers: [
        {
          id: Date.now().toString(),
          document: "",
          documentInputType: "inp",
          email: "",
          emailInputType: "inp",
          roleId: undefined,
          roleInputType: "inp",
        },
      ],
    };
  }

  // Parse users from data (legacy format)
  const dataInput = node.inputs?.find((input) => input.name === "data");
  let users: Array<{ document?: string; email?: string; role?: string }> = [];

  if (dataInput?.value) {
    try {
      const parsed =
        typeof dataInput.value === "string"
          ? JSON.parse(dataInput.value)
          : dataInput.value;
      users = parsed.users || [];
    } catch (e) {
      console.error("[getDeserializedLinkUsers] Error parsing data:", e);
    }
  }

  if (users.length === 0) {
    return {
      linkAction,
      linkUsers: [
        {
          id: Date.now().toString(),
          document: "",
          documentInputType: "inp",
          email: "",
          emailInputType: "inp",
          roleId: undefined,
          roleInputType: "inp",
        },
      ],
    };
  }

  // Flatten all variables for easy lookup
  const allVariables = inputVariables.flatMap((nodeVar) => nodeVar.variables);

  const linkUsers: LinkUserEntry[] = users.map((user, index) => {
    // Check if role is a variable (mustache syntax)
    let roleId: string | Variable | undefined;
    let roleInputType: "inp" | "var" = "inp";
    if (user.role) {
      const roleMatch = user.role.match(/^{{(.+)}}$/);
      if (roleMatch) {
        const variableName = roleMatch[1].trim();
        const variable = allVariables.find((v) => v.name === variableName);
        if (variable) {
          roleId = variable;
          roleInputType = "var";
        } else {
          // If variable not found, try to find the role by name
          if (entityRoles.length > 0) {
            const role = entityRoles.find((r) => r.name === user.role);
            if (role) {
              roleId = role.id;
            }
          }
          roleInputType = "inp";
        }
      } else if (entityRoles.length > 0) {
        const role = entityRoles.find((r) => r.name === user.role);
        if (role) {
          roleId = role.id;
        }
        roleInputType = "inp";
      }
    }

    // Check if document is a variable (mustache syntax)
    let document: string | Variable = "";
    let documentInputType: "inp" | "var" = "inp";
    if (user.document) {
      const docMatch = user.document.match(/^{{(.+)}}$/);
      if (docMatch) {
        const variableName = docMatch[1].trim();
        const variable = allVariables.find((v) => v.name === variableName);
        if (variable) {
          document = variable;
          documentInputType = "var";
        } else {
          // If variable not found, treat as input
          document = user.document;
          documentInputType = "inp";
        }
      } else {
        document = user.document;
        documentInputType = "inp";
      }
    }

    // Check if email is a variable (mustache syntax)
    let email: string | Variable = "";
    let emailInputType: "inp" | "var" = "inp";
    if (user.email) {
      const emailMatch = user.email.match(/^{{(.+)}}$/);
      if (emailMatch) {
        const variableName = emailMatch[1].trim();
        const variable = allVariables.find((v) => v.name === variableName);
        if (variable) {
          email = variable;
          emailInputType = "var";
        } else {
          // If variable not found, treat as input
          email = user.email;
          emailInputType = "inp";
        }
      } else {
        email = user.email;
        emailInputType = "inp";
      }
    }

    return {
      id: `${Date.now()}-${index}`,
      document,
      documentInputType,
      email,
      emailInputType,
      roleId,
      roleInputType,
    };
  });

  return { linkAction, linkUsers };
};

/**
 * Deserialize link users from new flat format with positional inputs
 */
const deserializeLinkUsersFromFlatFormat = (
  node: ApiNode,
  entityRoles: EntityRoleType[],
  inputVariables: NodeVariable[],
  linkAction: LinkActionType,
): { linkAction: LinkActionType; linkUsers: LinkUserEntry[] } => {
  const userDocumentInputs =
    node.inputs?.filter((input) => input.name === "user_document") || [];
  const userEmailInputs =
    node.inputs?.filter((input) => input.name === "user_email") || [];
  const userRoleInputs =
    node.inputs?.filter((input) => input.name === "user_role") || [];

  if (userDocumentInputs.length === 0) {
    return {
      linkAction,
      linkUsers: [
        {
          id: Date.now().toString(),
          document: "",
          documentInputType: "inp",
          email: "",
          emailInputType: "inp",
          roleId: undefined,
          roleInputType: "inp",
        },
      ],
    };
  }

  const linkUsers: LinkUserEntry[] = userDocumentInputs.map(
    (docInput, index) => {
      const emailInput = userEmailInputs[index];
      const roleInput = userRoleInputs[index];

      // Document
      let document: string | Variable = "";
      let documentInputType: "inp" | "var" = "inp";
      if (hasVariableConnection(docInput)) {
        const variable = resolveVariableFromInput(docInput, inputVariables);
        if (variable) {
          document = variable;
          documentInputType = "var";
        }
      } else if (docInput.value) {
        document = String(docInput.value);
        documentInputType = "inp";
      }

      // Email
      let email: string | Variable = "";
      let emailInputType: "inp" | "var" = "inp";
      if (hasVariableConnection(emailInput)) {
        const variable = resolveVariableFromInput(emailInput, inputVariables);
        if (variable) {
          email = variable;
          emailInputType = "var";
        }
      } else if (emailInput?.value) {
        email = String(emailInput.value);
        emailInputType = "inp";
      }

      // Role
      let roleId: string | Variable | undefined;
      let roleInputType: "inp" | "var" = "inp";
      if (hasVariableConnection(roleInput)) {
        const variable = resolveVariableFromInput(roleInput, inputVariables);
        if (variable) {
          roleId = variable;
          roleInputType = "var";
        }
      } else if (roleInput?.value) {
        const roleName = String(roleInput.value);
        const role = entityRoles.find((r) => r.name === roleName);
        if (role) {
          roleId = role.id;
        }
        roleInputType = "inp";
      }

      return {
        id: `${Date.now()}-${index}`,
        document,
        documentInputType,
        email,
        emailInputType,
        roleId,
        roleInputType,
      };
    },
  );

  return { linkAction, linkUsers };
};

/**
 * Deserialize registry name from node data
 * Supports both legacy format (data.name) and new flat format (expedient_register_name input)
 * Extracts the registry_name value and determines if it's a variable or input
 *
 * @param node - The API node
 * @param inputVariables - Available input variables
 * @returns Object with registry name, input type, and variable if applicable
 */
export const getDeserializedRegistryName = (
  node: ApiNode,
  inputVariables: NodeVariable[],
): {
  registryName: string;
  registryNameInputType: "inp" | "var";
  registryNameVariable: Variable | null;
} => {
  // Check for new flat format first
  const registryNameInput = node.inputs?.find(
    (input) => input.name === "expedient_register_name",
  );

  if (registryNameInput) {
    // New flat format - check for variable connection (handles both legacy and source_connections format)
    if (hasVariableConnection(registryNameInput)) {
      // Variable reference
      const variable = resolveVariableFromInput(
        registryNameInput,
        inputVariables,
      );
      if (variable) {
        return {
          registryName: "",
          registryNameInputType: "var",
          registryNameVariable: variable,
        };
      }
    }
    // Direct input value
    return {
      registryName: registryNameInput.value?.toString() || "",
      registryNameInputType: "inp",
      registryNameVariable: null,
    };
  }

  // Legacy format
  const dataObject = getExpedienteDataFromNode(node);

  if (!dataObject || !dataObject.name) {
    return {
      registryName: "",
      registryNameInputType: "inp",
      registryNameVariable: null,
    };
  }

  const registryNameValue = dataObject.name;

  // Check if it's a variable (wrapped in mustache brackets)
  const variableMatch = registryNameValue.match(/^{{(.+)}}$/);
  if (variableMatch) {
    const variableName = variableMatch[1];
    // Find the variable in inputVariables
    for (const nodeVar of inputVariables) {
      const foundVar = nodeVar.variables.find((v) => v.name === variableName);
      if (foundVar) {
        return {
          registryName: "",
          registryNameInputType: "var",
          registryNameVariable: foundVar,
        };
      }
    }
    // Variable not found, treat as input
    return {
      registryName: registryNameValue,
      registryNameInputType: "inp",
      registryNameVariable: null,
    };
  }

  // It's a direct input value
  return {
    registryName: registryNameValue,
    registryNameInputType: "inp",
    registryNameVariable: null,
  };
};

/**
 * Deserialize link expedients (registros) data from node
 * @param node - The API node
 * @param inputVariables - Available input variables
 * @returns Object with linkExpedientsAction and linkExpedients array, or undefined
 */
export const getDeserializedLinkExpedients = (
  node: ApiNode,
  inputVariables: NodeVariable[],
):
  | {
      linkExpedientsAction: LinkExpedientsActionType;
      linkExpedients: LinkExpedientEntry[];
    }
  | undefined => {
  const operationInput = node.inputs?.find(
    (input) => input.name === "operation",
  );
  const operation = operationInput?.value?.toString().toUpperCase();

  if (operation !== "LINK_EXPEDIENTS" && operation !== "UNLINK_EXPEDIENTS") {
    return undefined;
  }

  const linkExpedientsAction: "link" | "unlink" =
    operation === "LINK_EXPEDIENTS" ? "link" : "unlink";

  // Check for expedient_document inputs (flat format)
  const expedientDocumentInputs =
    node.inputs?.filter((input) => input.name === "expedient_document") || [];

  if (expedientDocumentInputs.length === 0) {
    // No expedient documents found, return default
    return {
      linkExpedientsAction,
      linkExpedients: [
        {
          id: Date.now().toString(),
          expedientDocument: "",
          inputType: "inp",
        },
      ],
    };
  }

  // Flatten all variables for easy lookup
  const allVariables = inputVariables.flatMap((nodeVar) =>
    nodeVar.variables.map((v) => ({
      ...v,
      node_id: nodeVar.nodeId,
      ref_name: nodeVar.ref_name || "",
    })),
  );

  const linkExpedients = expedientDocumentInputs.map((input, index) => {
    const id = `${Date.now()}_${index}`;

    // Check if value is a direct string or needs variable lookup
    if (input.value && typeof input.value === "string") {
      const value = input.value;

      // Check if it's a variable (wrapped in mustache brackets)
      const variableMatch = value.match(/^{{(.+)}}$/);
      if (variableMatch) {
        const variableName = variableMatch[1];
        const foundVar = allVariables.find((v) => v.name === variableName);
        if (foundVar) {
          return {
            id,
            expedientDocument: foundVar,
            inputType: "var" as const,
          };
        }
      }

      // Direct input value
      return {
        id,
        expedientDocument: value,
        inputType: "inp" as const,
      };
    }

    // Check for source_connections (variable connection)
    if (input.source_connections && input.source_connections.length > 0) {
      const sourceConn = input.source_connections[0];
      const sourceNodeId = sourceConn.source_node_id;

      // Find the variable using source_node_id
      const nodeVar = inputVariables.find((nv) => nv.nodeId === sourceNodeId);

      if (nodeVar) {
        // Try to find by field_id first
        let foundVariable = null;
        if (sourceConn.field_id) {
          foundVariable = nodeVar.variables.find(
            (v) => v.id === sourceConn.field_id,
          );
        }
        // Try by output_name
        if (!foundVariable && sourceConn.output_name) {
          foundVariable = nodeVar.variables.find(
            (v) =>
              v.name === sourceConn.output_name ||
              v.id === sourceConn.output_name,
          );
        }
        // Try by source_node_output_id
        if (!foundVariable && sourceConn.source_node_output_id) {
          foundVariable = nodeVar.variables.find(
            (v) => v.id === sourceConn.source_node_output_id,
          );
        }

        if (foundVariable) {
          const variableWithNodeInfo = {
            ...foundVariable,
            node_id: nodeVar.nodeId,
            ref_name: nodeVar.ref_name || "",
          };
          return {
            id,
            expedientDocument: variableWithNodeInfo,
            inputType: "var" as const,
          };
        }
      }
    }

    // Default empty entry
    return {
      id,
      expedientDocument: "",
      inputType: "inp" as const,
    };
  });

  return {
    linkExpedientsAction,
    linkExpedients,
  };
};
